import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import "./ShareFormDialog.css";
import api from "../../../Services/api";
import { Toast } from "primereact/toast";
import copy from "copy-to-clipboard";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";

const ShareFormDialog = ({ visible, onHide, form }) => {
  const [step, setStep] = useState(1);
  const [hours, setHours] = useState(0);
  const toast = useRef(null);
  const [password, setPassword] = useState("");
  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/form/${form?.id}`;
  const shareUrlExcel = `${baseUrl}/form-excel/${form?.id}`;

  useEffect(() => {
    if (form) {
      setPassword(form.password || "");
      setHours(form.timer || 0);
    }
  }, [form]);

  useEffect(() => {
    if (!visible) {
      setStep(1);
    }
  }, [visible]);

  const downloadQRCode = (qrId, filename) => {
    const canvas = document.getElementById(qrId);
    if (canvas) {
      const pngUrl = canvas
        .toDataURL("image/png")
        .replace("image/png", "image/octet-stream");

      let downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = `${filename}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      toast.current.show({
        severity: "success",
        summary: "Download Successful",
        detail: `${filename}.png has been downloaded`,
        life: 3000,
      });
    } else {
      toast.current.show({
        severity: "error",
        summary: "Download Failed",
        detail: "Unable to download QR code",
        life: 3000,
      });
    }
  };

  const copyToClipboard = (text) => {
    copy(text);
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Link copied to clipboard",
      life: 3000,
    });
  };
  const incrementHours = () => {
    setHours(hours + 1);
  };

  const decrementHours = () => {
    if (hours > 0) {
      setHours(hours - 1);
    }
  };

  const handlePasswordChange = async () => {
    if (password && password.length < 6) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Password must be at least 6 characters long",
        life: 3000,
      });
      return false;
    }
    const newPassword = password === "" ? null : password;
    if (newPassword !== form.password) {
      try {
        await api.put(`/forms/update-password/${form.id}`, {
          password: newPassword,
        });
        form.password = newPassword;
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Password changed successfully",
          life: 3000,
        });
        return true;
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to change password",
          life: 3000,
        });
        return false;
      }
    }
    return true;
  };

  const handleTimerChange = async () => {
    const newTimer = hours === 0 ? null : hours;
    const currentTimer = form.timer === 0 ? null : form.timer;

    if (newTimer !== currentTimer) {
      try {
        const response = await api.put(`/forms/set-timer/${form.id}`, {
          timer: newTimer,
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: response.data.message,
          life: 3000,
        });
        form.timer = newTimer;
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Error setting timer",
          life: 3000,
        });
        console.error("Error setting timer:", error);
      }
    }
  };
  const handleShare = async () => {
    const passwordValid = await handlePasswordChange();

    if (!passwordValid) {
      return;
    }

    await handleTimerChange();
    //copyToClipboard();
    setStep(2);
  };

  return (
    <Dialog
      header="Share Form"
      visible={visible}
      style={{ width: window.innerWidth > 768 ? "40vw" : "95vw" }}
      onHide={onHide}
      className="share-form-dialog"
    >
      <Toast ref={toast} />
      {step === 1 && (
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="timer">Set Timer (hours)</label>
            <div className="p-inputgroup">
              <InputText
                id="timer"
                value={hours}
                readOnly
                style={{ width: "50px", textAlign: "center" }}
              />
              <Button
                icon="pi pi-plus"
                onClick={incrementHours}
                style={{
                  marginTop: "0.5rem",
                  backgroundColor: "#D97A07",
                  color: "#fff",
                  border: "#D97A07",
                }}
              />
              <Button
                icon="pi pi-minus"
                onClick={decrementHours}
                style={{
                  marginTop: "0.5rem",
                  backgroundColor: "#D97A07",
                  color: "#fff",
                  border: "#D97A07",
                }}
              />
            </div>
          </div>
          <div className="p-field">
            <label htmlFor="password">Set Password</label>
            <Password
              toggleMask
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              feedback={false}
            />
          </div>
          <div
            className="p-field"
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              label="Share"
              onClick={handleShare}
              style={{
                backgroundColor: "#0d2e4d",
                color: "#fff",
                marginTop: "1rem",
                border: "#0d2e4d",
                width: "20%",
              }}
            />
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="shareUrl">Form URL</label>
            <div
              className="p-field p-grid"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <InputText
                id="shareUrl"
                value={shareUrl}
                readOnly
                style={{ flex: 1, marginRight: "10px" }}
              />
              <Button
                icon="pi pi-copy"
                onClick={() => copyToClipboard(shareUrl)}
                style={{
                  flexShrink: 0,
                  backgroundColor: "#fff",
                  color: "#D97A07",
                  border: "none",
                }}
              />
            </div>
            <div
              className="qr-code-container"
              style={{ textAlign: "center", marginBottom: "1rem" }}
            >
              <QRCodeSVG value={shareUrl} size={150} />
              <QRCodeCanvas
                id="qr-form"
                value={shareUrl}
                size={150}
                style={{ display: "none" }}
              />

              <div style={{ marginTop: "0.5rem" }}>
                <Button
                  label="Download QR Code for Form"
                  icon="pi pi-download"
                  onClick={() =>
                    downloadQRCode("qr-form", `form-${form?.label}-qr`)
                  }
                  style={{
                    backgroundColor: "#0d2e4d",
                    color: "#fff",
                    border: "#0d2e4d",
                  }}
                />
              </div>
            </div>
          </div>

          <div className="p-field">
            <label htmlFor="shareUrlExcel">Excel Form URL</label>
            <div
              className="p-field p-grid"
              style={{ display: "flex", alignItems: "center" }}
            >
              <InputText
                id="shareUrlExcel"
                value={shareUrlExcel}
                readOnly
                style={{ flex: 1, marginRight: "10px" }}
              />
              <Button
                icon="pi pi-copy"
                onClick={() => copyToClipboard(shareUrlExcel)}
                style={{
                  flexShrink: 0,
                  backgroundColor: "#fff",
                  color: "#D97A07",
                  border: "none",
                }}
              />
            </div>
            <div
              className="qr-code-container"
              style={{ textAlign: "center", marginTop: "1rem" }}
            >
              <QRCodeCanvas
                id="qr-excel"
                value={shareUrlExcel}
                size={150}
                style={{ display: "none" }}
              />
              <QRCodeCanvas id="qr-excel" value={shareUrlExcel} size={150} />
              <div style={{ marginTop: "0.5rem" }}>
                <Button
                  label="Download QR Code for Excel"
                  icon="pi pi-download"
                  onClick={() =>
                    downloadQRCode("qr-excel", `form-excel-${form?.label}-qr`)
                  }
                  style={{
                    backgroundColor: "#0d2e4d",
                    color: "#fff",
                    border: "#0d2e4d",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ShareFormDialog;
